// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, Users, RefreshCw, Eye, Edit2, X, Crown, Plus, Trash2, Gift, Key } from 'lucide-react';
import { toast } from 'sonner';
import EditableUserDetailDrawer from '@/components/admin-dashboard/EditableUserDetailDrawer';
import { EditableProfile, ProfileRole } from '@/types/profileTypes';
import { useRolesAndMemberships } from '@/hooks/useRolesAndMemberships';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import GiftMembershipDialog from '@/components/admin/GiftMembershipDialog';
import ViewAsButton from '@/components/admin/ViewAsButton';
import PaginationComponent from '@/components/common/PaginationComponent';
import AdminPasswordReset from '@/components/admin/users/AdminPasswordReset';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { getSessionUser } from '@/lib/authSession';

interface UserProfile {
  id: string;
  name: string;
  email: string;
  role: string;
  membership_level: string;
  status: string;
  created_at: string;
  updated_at?: string;
  last_login?: string;
  user_id: string;
  profile_completion: number;
  subscription_status?: string;
  trial_end_date?: string;
  payment_failures?: number;
  coach_verification_status?: string;
  username: string;
  phone: string;
  city: string;
  state: string;
  gender: string;
  birthdate: string;
  grad_year: number;
  sport_name: string;
  sport_id: string;
  height_inches: number;
  weight_lbs: number;
  position: string;
  education_level: string;
  personal_statement: string;
  primary_role_slug: string;
  active_membership_slug: string;
  commission_rate: number;
  gpa?: number;
  profile_photo_url?: string;
  cover_photo_url?: string;
  bio?: string;
  achievements?: string;
  is_verified?: boolean;
  verification_requested?: boolean;
  college_id?: string;
  role_assignments?: Array<{ role_slug: string; is_active: boolean }>;
  profile_memberships?: Array<{ membership_slug: string; is_active: boolean }>;
  user_roles?: string[];
  user_memberships?: string[];
}

const AdminUsersUnified: React.FC = () => {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [membershipFilter, setMembershipFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [gradYearFilter, setGradYearFilter] = useState('all');
  const [availableGradYears, setAvailableGradYears] = useState<number[]>([]);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<EditableProfile | null>(null);
  const [isUserDetailOpen, setIsUserDetailOpen] = useState(false);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'athlete',
    membership: 'free'
  });
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [profileMemberships, setProfileMemberships] = useState<Record<string, any[]>>({});
  const [passwordResetUser, setPasswordResetUser] = useState<UserProfile | null>(null);
  const [isPasswordResetOpen, setIsPasswordResetOpen] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(0);

  const { roles, memberships, isLoading: rolesLoading } = useRolesAndMemberships();

  const statusOptions = ['active', 'inactive', 'banned', 'pending'];

  useEffect(() => {
    fetchUsers();
  }, [currentPage, pageSize, searchTerm, roleFilter, membershipFilter, statusFilter, gradYearFilter, sortField, sortDirection]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, roleFilter, membershipFilter, statusFilter, gradYearFilter]);

  // Fetch available grad years for filter
  useEffect(() => {
    fetchAvailableGradYears();
  }, []);

  const fetchAvailableGradYears = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('grad_year')
        .not('grad_year', 'is', null)
        .gte('grad_year', 1900)
        .lte('grad_year', 2050);
      
      if (error) {
        console.error('Error fetching grad years:', error);
        return;
      }
      
      const uniqueYears = [...new Set(data.map(item => item.grad_year))].sort((a, b) => b - a);
      setAvailableGradYears(uniqueYears);
    } catch (err) {
      console.error('Error fetching grad years:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      
      console.log('🔍 Starting paginated user fetch...', { currentPage, pageSize });
      
      // Check authentication state first
      const { data: { user }, error: authError } = await getSessionUser();
      
      if (!user) {
        console.error('🔍 No authenticated user found');
        toast.error('Authentication required. Please log in again.');
        setIsLoading(false);
        return;
      }
      
      // Check if the membership filter is for a Press add-on
      let isPressAddonFilter = false;
      if (membershipFilter !== 'all') {
        const selectedMembership = memberships.find(m => m.slug === membershipFilter);
        isPressAddonFilter = selectedMembership?.is_addon === true && 
                            (selectedMembership?.role_slug === 'press' || selectedMembership?.role_slug === 'vendor_press');
      }

      // If filtering by Press add-on, we need to query profile_memberships
      if (isPressAddonFilter) {
        console.log('🔍 Filtering by Press add-on:', membershipFilter);
        
        // First, get all profile IDs that have this active Press membership
        const { data: pressMembershipData, error: pressMembershipError } = await supabase
          .from('profile_memberships')
          .select('profile_id')
          .eq('membership_slug', membershipFilter)
          .eq('is_active', true);

        if (pressMembershipError) {
          console.error('🔍 Press membership query error:', pressMembershipError);
          toast.error(`Database access error: ${pressMembershipError.message}`);
          setIsLoading(false);
          return;
        }

        const profileIds = pressMembershipData?.map(pm => pm.profile_id) || [];
        console.log('🔍 Found', profileIds.length, 'profiles with active Press membership');

        if (profileIds.length === 0) {
          // No users found with this Press membership
          setUsers([]);
          setTotalCount(0);
          setTotalPages(0);
          setIsLoading(false);
          return;
        }

        // Build query with filters for profiles with Press memberships
        let countQuery = supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true })
          .in('id', profileIds);
        
        let dataQuery = supabase
          .from('profiles')
          .select('*')
          .in('id', profileIds)
          .order(sortField, { ascending: sortDirection === 'asc' })
          .range((currentPage - 1) * pageSize, currentPage * pageSize - 1);
        
        // Apply other filters
        if (searchTerm) {
          const searchFilter = `name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,username.ilike.%${searchTerm}%`;
          countQuery = countQuery.or(searchFilter);
          dataQuery = dataQuery.or(searchFilter);
        }
        
        if (roleFilter !== 'all') {
          countQuery = countQuery.eq('role', roleFilter);
          dataQuery = dataQuery.eq('role', roleFilter);
        }
        
        if (statusFilter !== 'all') {
          countQuery = countQuery.eq('status', statusFilter);
          dataQuery = dataQuery.eq('status', statusFilter);
        }
        
        if (gradYearFilter !== 'all') {
          countQuery = countQuery.eq('grad_year', parseInt(gradYearFilter));
          dataQuery = dataQuery.eq('grad_year', parseInt(gradYearFilter));
        }
        
        // Execute both queries
        const [{ count, error: countError }, { data, error: dataError }] = await Promise.all([
          countQuery,
          dataQuery
        ]);
        
        if (countError) {
          console.error('🔍 Count query error:', countError);
          toast.error(`Database access error: ${countError.message}`);
          return;
        }
        
        if (dataError) {
          console.error('🔍 Data query error:', dataError);
          toast.error(`Database access error: ${dataError.message}`);
          return;
        }
        
        if (!data) {
          console.log('🔍 No data returned');
          toast.error('No data returned from database');
          return;
        }
        
        console.log(`🔍 Fetched ${data.length} Press members for page ${currentPage} (total: ${count})`);
        
        // Process users with all necessary fields
        const processedUsers = data.map(user => ({
          ...user,
          user_roles: [user.role || 'athlete'],
          user_memberships: [user.active_membership_slug || 'free'],
          primary_role_slug: user.primary_role_slug || user.role || 'athlete',
          active_membership_slug: user.active_membership_slug || user.membership_level || 'free',
          commission_rate: user.commission_rate || 0,
          name: user.name || '',
          email: user.email || '',
          status: user.status || 'active',
          created_at: user.created_at || new Date().toISOString()
        }));

        setUsers(processedUsers);
        setTotalCount(count || 0);
        setTotalPages(Math.ceil((count || 0) / pageSize));
        setProfileMemberships({});
        
        console.log('🔍 Press membership filter completed successfully');
        setIsLoading(false);
        return;
      }

      // Standard query for non-Press filters
      let countQuery = supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });
      
      let dataQuery = supabase
        .from('profiles')
        .select('*')
        .order(sortField, { ascending: sortDirection === 'asc' })
        .range((currentPage - 1) * pageSize, currentPage * pageSize - 1);
      
      // Apply filters
      if (searchTerm) {
        const searchFilter = `name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,username.ilike.%${searchTerm}%`;
        countQuery = countQuery.or(searchFilter);
        dataQuery = dataQuery.or(searchFilter);
      }
      
      if (roleFilter !== 'all') {
        countQuery = countQuery.eq('role', roleFilter);
        dataQuery = dataQuery.eq('role', roleFilter);
      }
      
      if (membershipFilter !== 'all') {
        countQuery = countQuery.eq('active_membership_slug', membershipFilter);
        dataQuery = dataQuery.eq('active_membership_slug', membershipFilter);
      }
      
      if (statusFilter !== 'all') {
        countQuery = countQuery.eq('status', statusFilter);
        dataQuery = dataQuery.eq('status', statusFilter);
      }
      
      if (gradYearFilter !== 'all') {
        countQuery = countQuery.eq('grad_year', parseInt(gradYearFilter));
        dataQuery = dataQuery.eq('grad_year', parseInt(gradYearFilter));
      }
      
      // Execute both queries
      const [{ count, error: countError }, { data, error: dataError }] = await Promise.all([
        countQuery,
        dataQuery
      ]);
      
      if (countError) {
        console.error('🔍 Count query error:', countError);
        toast.error(`Database access error: ${countError.message}`);
        return;
      }
      
      if (dataError) {
        console.error('🔍 Data query error:', dataError);
        toast.error(`Database access error: ${dataError.message}`);
        return;
      }
      
      if (!data) {
        console.log('🔍 No data returned');
        toast.error('No data returned from database');
        return;
      }
      
      console.log(`🔍 Fetched ${data.length} users for page ${currentPage} (total: ${count})`);
      
      // Process users with all necessary fields
      const processedUsers = data.map(user => ({
        ...user,
        user_roles: [user.role || 'athlete'],
        user_memberships: [user.active_membership_slug || 'free'],
        primary_role_slug: user.primary_role_slug || user.role || 'athlete',
        active_membership_slug: user.active_membership_slug || user.membership_level || 'free',
        commission_rate: user.commission_rate || 0,
        // Ensure all required fields have defaults
        name: user.name || '',
        email: user.email || '',
        status: user.status || 'active',
        created_at: user.created_at || new Date().toISOString()
      }));

      setUsers(processedUsers);
      setTotalCount(count || 0);
      setTotalPages(Math.ceil((count || 0) / pageSize));
      setProfileMemberships({});
      
      console.log('🔍 Paginated user fetch completed successfully');
    } catch (err) {
      console.error('🔍 Unexpected error in fetchUsers:', err);
      toast.error(`Unexpected error: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // No longer need client-side filtering since we're doing server-side filtering

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <span className="opacity-30">↕️</span>;
    }
    return sortDirection === 'asc' ? <span className="text-primary">↑</span> : <span className="text-primary">↓</span>;
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: string) => {
    setPageSize(parseInt(newPageSize));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const formatRoleDisplay = (role: string) => {
    return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleUserClick = (userId: string) => {
    const user = users.find(u => u.id === userId);
    if (user) {
      setSelectedUser({
        id: user.id,
        user_id: user.user_id,
        name: user.name,
        email: user.email,
        username: user.username || '',
        phone: user.phone || '',
        city: user.city || '',
        state: user.state || '',
        country: '',
        birthdate: user.birthdate || '',
        sport: user.sport_name,
        sport_id: user.sport_id,
        sport_name: user.sport_name,
        position: user.position || '',
        height_inches: user.height_inches,
        weight_lbs: user.weight_lbs || 0,
        grad_year: user.grad_year,
        personal_statement: user.personal_statement || '',
        avatar_url: user.profile_photo_url || '',
        profile_completion: user.profile_completion || 0,
        education_level: user.education_level || '',
        role: user.role as ProfileRole,
        membership_level: user.active_membership_slug || 'free',
        status: user.status || 'active',
        created_at: user.created_at,
        gender: user.gender || ''
      });
      setIsUserDetailOpen(true);
    }
  };

  const handleStatusChange = async (userId: string, newStatus: string) => {
    setIsUpdating(userId);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ status: newStatus })
        .eq('id', userId);

      if (error) {
        console.error('Error updating status:', error);
        toast.error('Failed to update user status');
        return;
      }

      toast.success('User status updated successfully');
      fetchUsers();
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('An unexpected error occurred');
    } finally {
      setIsUpdating(null);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    setIsUpdating(userId);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ role: newRole })
        .eq('id', userId);

      if (error) {
        console.error('Error updating role:', error);
        toast.error('Failed to update user role');
        return;
      }

      toast.success('User role updated successfully');
      fetchUsers();
    } catch (err) {
      console.error('Error updating role:', err);
      toast.error('An unexpected error occurred');
    } finally {
      setIsUpdating(null);
    }
  };

  const handleCreateUser = async () => {
    if (!newUserForm.name || !newUserForm.email || !newUserForm.password) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsCreatingUser(true);
    try {
      const requestPayload = { 
        action: 'create_user',
        email: newUserForm.email,
        temp_password: newUserForm.password,
        userData: {
          name: newUserForm.name,
          role: newUserForm.role
        }
      };

      console.log('🔍 Admin auth request payload:', requestPayload);
      
      // Use the admin-auth-management edge function instead of direct signup
      const { data, error } = await invokeEdgeFunction('admin-auth-management', {
        body: requestPayload
      });

      console.log('🔍 Edge function raw response:', { data, error });

      if (error) {
        console.error('🔍 Full error details:', {
          message: error.message,
          name: error.name,
          stack: error.stack,
          cause: error.cause,
          status: error.status,
          details: error.details,
          context: error.context
        });
        
        // Try to get more details from the error
        if (error.message?.includes('non-2xx status code')) {
          console.error('🔍 This is a HTTP status error - the function returned an error status');
          toast.error('Server returned an error. Check the edge function logs for details.');
        } else if (error.message?.includes('already exists')) {
          toast.error(`A user with email ${newUserForm.email} already exists in the system`);
        } else {
          toast.error(`Failed to create user: ${error.message}`);
        }
        return;
      }

      // Check if the response indicates an error
      if (data && !data.success) {
        console.error('🔍 Function returned error:', data.error);
        if (data.error.includes('already exists')) {
          toast.error(`A user with email ${newUserForm.email} already exists in the system`);
        } else {
          toast.error(`Failed to create user: ${data.error}`);
        }
        return;
      }

      if (!data?.success) {
        console.error('🔍 Unexpected response format:', data);
        toast.error('Unexpected response from server');
        return;
      }

      // Update the profile with the selected membership
      if (data?.profile_id && newUserForm.membership !== 'editor') {
        const { error: membershipError } = await supabase
          .from('profiles')
          .update({
            active_membership_slug: newUserForm.membership
          })
          .eq('id', data.profile_id);

        if (membershipError) {
          console.error('Membership update error:', membershipError);
          // Don't fail the creation for this, just warn
          toast.warning('User created but membership update failed');
        }
      }

      toast.success('User created successfully');
      setIsAddUserOpen(false);
      setNewUserForm({
        name: '',
        email: '',
        password: '',
        role: 'athlete',
        membership: 'free'
      });
      fetchUsers();
    } catch (err) {
      console.error('Error creating user:', err);
      toast.error('An unexpected error occurred');
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleSelectUser = (userId: string, checked: boolean) => {
    if (checked) {
      setSelectedUserIds(prev => [...prev, userId]);
    } else {
      setSelectedUserIds(prev => prev.filter(id => id !== userId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUserIds(users.map(user => user.id));
    } else {
      setSelectedUserIds([]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUserIds.length === 0) {
      toast.error('No users selected');
      return;
    }

    if (!confirm(`Are you sure you want to delete ${selectedUserIds.length} users? This will permanently delete their accounts and all associated data. This action cannot be undone.`)) {
      return;
    }

    setIsBulkDeleting(true);
    try {
      console.log('🗑️ Calling delete-user edge function for:', selectedUserIds);
      
      const { data, error } = await invokeEdgeFunction('delete-user', {
        body: { userIds: selectedUserIds }
      });

      if (error) {
        console.error('Error calling delete-user function:', error);
        toast.error(`Failed to delete users: ${error.message}`);
        return;
      }

      console.log('✅ Delete response:', data);

      if (data.errors && data.errors.length > 0) {
        toast.error(`Deleted ${data.deleted} users, but ${data.failed} failed. Check console for details.`);
        console.error('❌ Deletion errors:', data.errors);
        // Log each error individually for clarity
        data.errors.forEach((err: any, index: number) => {
          console.error(`   Error ${index + 1}:`, {
            profileId: err.profileId,
            userId: err.userId,
            error: err.error
          });
        });
      } else {
        toast.success(`Successfully deleted ${data.deleted} user(s)`);
      }

      setSelectedUserIds([]);
      fetchUsers();
    } catch (err) {
      console.error('Error deleting users:', err);
      toast.error('An unexpected error occurred while deleting users');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const renderUserTable = () => {
    if (isLoading) {
      return (
        <div className="space-y-3">
          {[...Array(10)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      );
    }

    if (users.length === 0) {
      return (
        <div className="text-center py-8">
          <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No users found</h3>
          <p className="text-muted-foreground">
            Try adjusting your search or filter criteria.
          </p>
        </div>
      );
    }

    return (
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={selectedUserIds.length === users.length && users.length > 0}
                  onCheckedChange={handleSelectAll}
                />
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('name')}>
                Name {getSortIcon('name')}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('email')}>
                Email {getSortIcon('email')}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('role')}>
                Role {getSortIcon('role')}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('status')}>
                Status {getSortIcon('status')}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('active_membership_slug')}>
                Membership {getSortIcon('active_membership_slug')}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('profile_completion')}>
                Completion {getSortIcon('profile_completion')}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => handleSort('created_at')}>
                Created {getSortIcon('created_at')}
              </TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <React.Fragment key={user.id}>
                <TableRow className="cursor-pointer hover:bg-muted/50">
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedUserIds.includes(user.id)}
                      onCheckedChange={(checked) => handleSelectUser(user.id, checked as boolean)}
                    />
                  </TableCell>
                  <TableCell onClick={() => setExpandedUserId(expandedUserId === user.id ? null : user.id)}>
                    <div className="flex items-center gap-2">
                      {user.role === 'admin' && <Crown className="h-4 w-4 text-yellow-500" />}
                      <span className="font-medium">{user.name || 'No Name'}</span>
                    </div>
                  </TableCell>
                  <TableCell onClick={() => setExpandedUserId(expandedUserId === user.id ? null : user.id)}>
                    {user.email || 'No Email'}
                  </TableCell>
                  <TableCell onClick={() => setExpandedUserId(expandedUserId === user.id ? null : user.id)}>
                      <Badge variant="outline">
                        {formatRoleDisplay(user.role || 'athlete')}
                      </Badge>
                  </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Select
                    value={user.status || 'active'}
                    onValueChange={(value) => handleStatusChange(user.id, value)}
                    disabled={isUpdating === user.id}
                  >
                    <SelectTrigger className="w-[120px] h-8">
                      <SelectValue>
                        <Badge variant={user.status === 'active' ? 'default' : user.status === 'banned' ? 'destructive' : 'secondary'}>
                          {user.status || 'active'}
                        </Badge>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">
                        <Badge variant="default">Active</Badge>
                      </SelectItem>
                      <SelectItem value="inactive">
                        <Badge variant="secondary">Inactive</Badge>
                      </SelectItem>
                      <SelectItem value="banned">
                        <Badge variant="destructive">Banned</Badge>
                      </SelectItem>
                      <SelectItem value="pending">
                        <Badge variant="secondary">Pending</Badge>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell onClick={() => handleUserClick(user.id)}>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">
                      {user.active_membership_slug || 'free'}
                    </Badge>
                    {profileMemberships[user.id]?.length > 0 && (
                      <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700">
                        🎁 Promo
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell onClick={() => handleUserClick(user.id)}>
                  <div className="flex items-center gap-2">
                    <div className="w-16 bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all" 
                        style={{ width: `${user.profile_completion || 0}%` }}
                      />
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {user.profile_completion || 0}%
                    </span>
                  </div>
                </TableCell>
                <TableCell onClick={() => handleUserClick(user.id)}>
                  {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleUserClick(user.id)}
                      title="Edit user details"
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <ViewAsButton
                      profileId={user.id}
                      userName={user.name}
                      variant="ghost"
                      size="sm"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setPasswordResetUser(user);
                        setIsPasswordResetOpen(true);
                      }}
                      title="Reset password"
                    >
                      <Key className="h-4 w-4" />
                    </Button>
                    <GiftMembershipDialog
                      userId={user.id}
                      userName={user.name || 'User'}
                      onSuccess={fetchUsers}
                    />
                  </div>
                </TableCell>
              </TableRow>
              {expandedUserId === user.id && (
                <TableRow>
                  <TableCell colSpan={9} className="bg-muted/30 p-0">
                    <div className="p-6">
                      <EditableUserDetailDrawer
                        isOpen={true}
                        onClose={() => setExpandedUserId(null)}
                        user={user as unknown as EditableProfile}
                        onUserUpdate={(updatedUser) => {
                          setUsers(prevUsers => 
                            prevUsers.map(u => u.id === updatedUser.id ? { ...u, ...updatedUser } : u)
                          );
                          setExpandedUserId(null);
                        }}
                        onDataRefresh={fetchUsers}
                        inline={true}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary-700">User Management</h1>
          <p className="text-muted-foreground">Manage all user accounts and permissions</p>
        </div>
        <div className="flex gap-2">
          {selectedUserIds.length > 0 && (
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={isBulkDeleting}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Selected ({selectedUserIds.length})
            </Button>
          )}
          <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={newUserForm.name}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter full name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Enter email address"
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newUserForm.password}
                    onChange={(e) => setNewUserForm(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Enter password"
                  />
                </div>
                <div>
                  <Label htmlFor="role">Role</Label>
                  <Select
                    value={newUserForm.role}
                    onValueChange={(value) => setNewUserForm(prev => ({ ...prev, role: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role.slug} value={role.slug}>
                          {role.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="membership">Membership</Label>
                  <Select
                    value={newUserForm.membership}
                    onValueChange={(value) => setNewUserForm(prev => ({ ...prev, membership: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {memberships.map((membership) => (
                        <SelectItem key={membership.slug} value={membership.slug}>
                          {membership.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsAddUserOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateUser} disabled={isCreatingUser}>
                    {isCreatingUser ? 'Creating...' : 'Create User'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <Button variant="outline" onClick={fetchUsers} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            All Users ({users.length} of {totalCount})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or username..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {roles.map((role) => (
                  <SelectItem key={role.slug} value={role.slug}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={membershipFilter} onValueChange={setMembershipFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by membership" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Memberships</SelectItem>
                {memberships.map((membership) => (
                  <SelectItem key={membership.slug} value={membership.slug}>
                    {membership.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statusOptions.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={gradYearFilter} onValueChange={setGradYearFilter}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Grad Year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {availableGradYears.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(searchTerm || roleFilter !== 'all' || membershipFilter !== 'all' || statusFilter !== 'all' || gradYearFilter !== 'all') && (
              <Button variant="outline" onClick={() => {
                setSearchTerm('');
                setRoleFilter('all');
                setMembershipFilter('all');
                setStatusFilter('all');
                setGradYearFilter('all');
              }}>
                <X className="h-4 w-4 mr-2" />
                Clear Filters
              </Button>
            )}
          </div>
          
          {renderUserTable()}
          
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Show:</span>
                <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="500">500</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">per page</span>
              </div>
              <span className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} users
              </span>
            </div>
            
            {totalPages > 1 && (
              <PaginationComponent
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </CardContent>
      </Card>

      {selectedUser && (
        <EditableUserDetailDrawer
          isOpen={isUserDetailOpen}
          onClose={() => {
            setIsUserDetailOpen(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          onUserUpdate={(updatedUser) => {
            // Don't close drawer immediately - let user see the changes
            fetchUsers(); // Refresh the data in background
          }}
          onDataRefresh={fetchUsers}
        />
      )}

      <AdminPasswordReset
        userId={passwordResetUser?.id}
        userEmail={passwordResetUser?.email}
        userName={passwordResetUser?.name}
        open={isPasswordResetOpen}
        onOpenChange={setIsPasswordResetOpen}
      />
    </div>
  );
};

export default AdminUsersUnified;